// Settings: project & booking rules, work areas, companies, users, backups, audit history, account.
import React, { useEffect, useState } from 'react';
import { api, download, currentUser } from '../api';
import { RegisterPage, FieldSpec, useLookup, dateCol } from '../crud';
import { Tabs, ErrorBox, Spinner, Confirm, Field, Modal } from '../ui';
import { toAus } from '../util';

export default function Settings() {
  const [tab, setTab] = useState('project');
  const isAdmin = currentUser?.role === 'admin';
  return (
    <div className="page">
      <Tabs active={tab} onSelect={setTab} tabs={[
        { key: 'project', label: 'Projects & booking rules' },
        { key: 'areas', label: 'Work areas' },
        { key: 'roles', label: 'Roles' },
        { key: 'trades', label: 'Trades' },
        { key: 'companies', label: 'Companies' },
        ...(isAdmin ? [{ key: 'users', label: 'Users' }, { key: 'backup', label: 'Backups' }] : []),
        { key: 'audit', label: 'Change history' },
        { key: 'account', label: 'My account' }
      ]} />
      {tab === 'project' && <Projects />}
      {tab === 'areas' && <WorkAreas />}
      {tab === 'roles' && <SimpleList entity="roles" title="Roles" hint="Position level, e.g. Site Supervisor. Used on the Personnel page." />}
      {tab === 'trades' && <SimpleList entity="trades" title="Trades" hint="The person's trade or discipline, e.g. Carpentry. Used on the Personnel page and in trade reports." />}
      {tab === 'companies' && <Companies />}
      {tab === 'users' && isAdmin && <Users />}
      {tab === 'backup' && isAdmin && <Backups />}
      {tab === 'audit' && <Audit />}
      {tab === 'account' && <Account />}
    </div>
  );
}

function Projects() {
  const providers = useLookup('providers');
  const specs: FieldSpec[] = [
    { name: 'name', label: 'Project name', type: 'text', required: true, section: 'Project' },
    { name: 'code', label: 'Project code', type: 'text', section: 'Project' },
    { name: 'client', label: 'Client', type: 'text', section: 'Project' },
    { name: 'location', label: 'Location', type: 'text', section: 'Project' },
    { name: 'timezone', label: 'Time zone', type: 'text', default: 'Australia/Darwin', section: 'Project' },
    { name: 'status', label: 'Status', type: 'select', options: ['Active', 'On hold', 'Completed'], default: 'Active', section: 'Project' },
    { name: 'start_date', label: 'Start date', type: 'date', section: 'Project' },
    { name: 'forecast_completion', label: 'Forecast completion', type: 'date', section: 'Project' },
    { name: 'project_manager', label: 'Project manager', type: 'text', section: 'Project' },
    { name: 'site_supervisor', label: 'Site supervisor', type: 'text', section: 'Project' },
    { name: 'default_provider_id', label: 'Default accommodation provider', type: 'select', lookup: 'providers', section: 'Booking rules' },
    { name: 'booking_lead_days', label: 'Standard booking lead time (days)', type: 'int', default: 60, section: 'Booking rules' },
    { name: 'preferred_lead_days', label: 'Preferred booking lead time (days)', type: 'int', default: 90, section: 'Booking rules' },
    { name: 'cancellation_notice_days', label: 'Cancellation notice (days)', type: 'int', default: 14, section: 'Booking rules' },
    { name: 'names_due_lead_days', label: 'Names must be confirmed (days before check-in)', type: 'int', default: 21, section: 'Booking rules' },
    { name: 'contingency_percent', label: 'Contingency room allowance (%)', type: 'real', default: 10, section: 'Booking rules' },
    { name: 'min_block_size', label: 'Minimum room block size', type: 'int', default: 1, section: 'Booking rules' },
    { name: 'booking_date_format', label: 'Date format on the booking schedule', type: 'select',
      options: ['DD/MM/YYYY', 'M/D/YYYY', 'D MMM YYYY', 'YYYY-MM-DD'], default: 'DD/MM/YYYY', section: 'Booking schedule sent to the provider' },
    { name: 'booking_room_prefix', label: 'Room label prefix', type: 'text', default: 'Room', section: 'Booking schedule sent to the provider' },
    { name: 'notes', label: 'Notes', type: 'textarea', section: 'Booking schedule sent to the provider' }
  ];
  return <RegisterPage title="Projects" entity="projects" specs={specs} lookupEntities={{ providers }} columns={[
    { key: 'name', label: 'Project', render: r => <strong>{r.name}</strong> },
    { key: 'code', label: 'Code' },
    { key: 'location', label: 'Location' },
    { key: 'status', label: 'Status' },
    dateCol('start_date', 'Start'),
    dateCol('forecast_completion', 'Forecast completion'),
    { key: 'booking_lead_days', label: 'Booking lead (days)' }
  ]} />;
}

function WorkAreas() {
  const projects = useLookup('projects');
  const specs: FieldSpec[] = [
    { name: 'project_id', label: 'Project', type: 'select', lookup: 'projects', required: true, default: projects.rows[0]?.id },
    { name: 'name', label: 'Work area / package', type: 'text', required: true },
    { name: 'sort_order', label: 'Sort order', type: 'int', default: 0 }
  ];
  return <RegisterPage title="Work areas" entity="work_areas" specs={specs} lookupEntities={{ projects }} canDelete columns={[
    { key: 'name', label: 'Work area', render: r => <strong>{r.name}</strong> },
    { key: 'project_id', label: 'Project', render: r => projects.byId[r.project_id] || '', sortValue: r => projects.byId[r.project_id] },
    { key: 'sort_order', label: 'Order' }
  ]} />;
}

// A maintained pick-list (roles, trades). Deactivating hides an entry from new dropdowns
// without disturbing personnel records that already use it.
function SimpleList({ entity, title, hint }: { entity: string; title: string; hint: string }) {
  const specs: FieldSpec[] = [
    { name: 'name', label: title.replace(/s$/, '') + ' name', type: 'text', required: true },
    { name: 'sort_order', label: 'Sort order (lower appears first)', type: 'int', default: 500 },
    { name: 'active', label: 'Active (appears in dropdowns)', type: 'bool', default: 1 }
  ];
  return (
    <div>
      <p className="muted" style={{ marginBottom: 10 }}>
        {hint} Changes appear in the dropdowns immediately — no restart needed. Existing
        personnel keep whatever is already recorded against them, even if you deactivate
        or delete an entry here.
      </p>
      <RegisterPage title={title} entity={entity} specs={specs} canDelete columns={[
        { key: 'name', label: title.replace(/s$/, ''), render: r => <strong>{r.name}</strong> },
        { key: 'sort_order', label: 'Order' },
        { key: 'active', label: 'Active', render: r => (r.active ? 'Yes' : 'No'), sortValue: r => r.active }
      ]} />
    </div>
  );
}

function Companies() {
  const specs: FieldSpec[] = [
    { name: 'name', label: 'Company name', type: 'text', required: true },
    { name: 'company_type', label: 'Type', type: 'select', options: ['Head contractor', 'Subcontractor', 'Labour hire', 'Consultant', 'Supplier', 'Client', 'Other'], default: 'Subcontractor' },
    { name: 'contact_name', label: 'Contact name', type: 'text' },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'active', label: 'Active', type: 'bool', default: 1 },
    { name: 'notes', label: 'Notes', type: 'textarea' }
  ];
  return <RegisterPage title="Companies" entity="companies" specs={specs} columns={[
    { key: 'name', label: 'Company', render: r => <strong>{r.name}</strong> },
    { key: 'company_type', label: 'Type' },
    { key: 'contact_name', label: 'Contact' },
    { key: 'phone', label: 'Phone' }
  ]} />;
}

function Users() {
  const [users, setUsers] = useState<any[] | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [error, setError] = useState<string[] | null>(null);
  const [form, setForm] = useState<any>({ kind: 'internal', role: 'planner', project_ids: [] });
  const [editing, setEditing] = useState<any | null>(null);

  const load = () => {
    api('/users').then(setUsers).catch(e => setError([e.message]));
    api('/projects').then(setProjects).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const isExternal = form.kind === 'external';

  const create = async () => {
    setError(null);
    const body: any = {
      username: form.username, password: form.password, display_name: form.display_name,
      company: form.company || null,
      role: isExternal ? 'readonly' : form.role,
      access_scope: isExternal ? 'projects' : 'all',
      project_ids: isExternal ? form.project_ids : [],
      can_confirm_bookings: isExternal ? !!form.can_confirm_bookings : false
    };
    try {
      await api('/users', { method: 'POST', body });
      setForm({ kind: 'internal', role: 'planner', project_ids: [] });
      load();
    } catch (e: any) { setError(e.errors?.length ? e.errors : [e.message]); }
  };

  const saveJobs = async (u: any, ids: number[]) => {
    setError(null);
    try {
      await api(`/users/${u.id}`, { method: 'PUT', body: { project_ids: ids } });
      setEditing(null);
      load();
    } catch (e: any) { setError(e.errors?.length ? e.errors : [e.message]); }
  };

  if (!users) return <Spinner />;
  return (
    <div>
      <h2>Users</h2>
      <ErrorBox error={error} onDismiss={() => setError(null)} />

      <div className="panel">
        <table>
          <thead><tr>
            <th>Username</th><th>Name</th><th>Access</th><th>Jobs</th>
            <th>Can confirm<br /><span style={{ fontWeight: 400, textTransform: 'none' }}>bookings</span></th>
            <th>Active</th><th></th>
          </tr></thead>
          <tbody>
            {users.map(u => {
              const scoped = u.access_scope === 'projects';
              return (
                <tr key={u.id}>
                  <td>{u.username}</td>
                  <td>{u.display_name}{u.company && <div className="muted">{u.company}</div>}</td>
                  <td>
                    {scoped ? (
                      <span className="chip" style={{ background: '#c98227' }}>external</span>
                    ) : (
                      <select className="input" value={u.role} onChange={async e => {
                        try { await api(`/users/${u.id}`, { method: 'PUT', body: { role: e.target.value } }); load(); }
                        catch (err: any) { setError(err.errors?.length ? err.errors : [err.message]); }
                      }}>
                        {['admin', 'planner', 'supervisor', 'readonly'].map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    )}
                  </td>
                  <td style={{ maxWidth: 260, whiteSpace: 'normal' }}>
                    {scoped ? (
                      <>
                        {u.projects.length ? u.projects.map((p: any) => p.name).join(', ')
                          : <span style={{ color: 'var(--danger)' }}>none — sees nothing</span>}
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(u)}>change</button>
                      </>
                    ) : <span className="muted">all jobs</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" className="checkbox" checked={!!u.can_confirm_bookings}
                      title="Allow this person to tick check-in/check-out cycles off as booked with the provider"
                      onChange={async e => {
                        try { await api(`/users/${u.id}`, { method: 'PUT', body: { can_confirm_bookings: e.target.checked } }); load(); }
                        catch (err: any) { setError(err.errors?.length ? err.errors : [err.message]); }
                      }} />
                    {!!u.can_confirm_bookings && <div className="muted" style={{ fontSize: 10 }}>yes</div>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" className="checkbox" checked={!!u.active} onChange={async e => {
                      try { await api(`/users/${u.id}`, { method: 'PUT', body: { active: e.target.checked } }); load(); }
                      catch (err: any) { setError(err.errors?.length ? err.errors : [err.message]); }
                    }} />
                  </td>
                  <td>
                    <button className="btn btn-sm" onClick={async () => {
                      const pw = window.prompt(`New password for ${u.username} (min 8 characters):`);
                      if (!pw) return;
                      try { await api(`/users/${u.id}`, { method: 'PUT', body: { password: pw } }); load(); }
                      catch (err: any) { setError(err.errors?.length ? err.errors : [err.message]); }
                    }}>Reset password</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="muted" style={{ padding: '8px 0 0' }}>
          Changing someone's role, job access or password signs them out immediately, so the
          new permissions take effect straight away rather than when their session expires.
        </p>
      </div>

      <div className="panel">
        <h3>Add user</h3>
        <div className="btn-row" style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13 }}>
            <input type="radio" checked={!isExternal} onChange={() => setForm({ ...form, kind: 'internal' })} />
            {' '}TPA staff — full access to every job
          </label>
          <label style={{ fontSize: 13, marginLeft: 16 }}>
            <input type="radio" checked={isExternal} onChange={() => setForm({ ...form, kind: 'external' })} />
            {' '}External — booking schedule for selected jobs only
          </label>
        </div>

        <div className="form-grid">
          <Field label="Username *"><input className="input" value={form.username || ''} onChange={e => setForm({ ...form, username: e.target.value })} /></Field>
          <Field label="Full name *"><input className="input" value={form.display_name || ''} onChange={e => setForm({ ...form, display_name: e.target.value })} /></Field>
          <Field label="Password * (min 8)"><input className="input" type="password" value={form.password || ''} onChange={e => setForm({ ...form, password: e.target.value })} /></Field>
          {isExternal ? (
            <Field label="Company" hint="Shown in the user list so it's clear who is external.">
              <input className="input" placeholder="e.g. Beckons" value={form.company || ''}
                onChange={e => setForm({ ...form, company: e.target.value })} />
            </Field>
          ) : (
            <Field label="Role" hint="admin: full access · planner: manage data · supervisor: confirm actuals & notes · readonly: view only">
              <select className="input" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                {['admin', 'planner', 'supervisor', 'readonly'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          )}
        </div>

        {isExternal && (
          <>
            <h4 className="form-section">Jobs this person may see *</h4>
            <p className="muted" style={{ marginTop: 0 }}>
              They can open the <strong>planning</strong> and <strong>accommodation</strong> pages,
              but only ever for the jobs ticked here — every list is filtered to those jobs on the
              server, not just hidden in the menu. They cannot see other jobs, reports, imports,
              backups, the audit history or user accounts, and cannot change any data.
            </p>
            <label style={{ display: 'block', fontSize: 13, margin: '8px 0' }}>
              <input type="checkbox" className="checkbox" checked={!!form.can_confirm_bookings}
                onChange={e => setForm({ ...form, can_confirm_bookings: e.target.checked })} />
              {' '}<strong>May confirm bookings</strong> — can tick each check-in/check-out cycle
              off as booked and record the provider's reference. Use this for whoever actually
              reserves the rooms. It is the only change they can make, and every tick is recorded
              against their name.
            </label>
            {projects.map(p => (
              <label key={p.id} style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
                <input type="checkbox" className="checkbox"
                  checked={(form.project_ids || []).includes(p.id)}
                  onChange={e => {
                    const next = new Set<number>(form.project_ids || []);
                    e.target.checked ? next.add(p.id) : next.delete(p.id);
                    setForm({ ...form, project_ids: [...next] });
                  }} />
                {' '}{p.name}{p.location ? <span className="muted"> — {p.location}</span> : ''}
              </label>
            ))}
          </>
        )}

        <div className="btn-row form-actions">
          <button className="btn btn-primary" onClick={create}>Create user</button>
        </div>
      </div>

      {editing && (
        <Modal title={`Jobs for ${editing.display_name}`} onClose={() => setEditing(null)}>
          <JobPicker user={editing} projects={projects} onSave={ids => saveJobs(editing, ids)} onCancel={() => setEditing(null)} />
        </Modal>
      )}
    </div>
  );
}

function JobPicker({ user, projects, onSave, onCancel }: {
  user: any; projects: any[]; onSave: (ids: number[]) => void; onCancel: () => void;
}) {
  const [ids, setIds] = useState<number[]>((user.projects || []).map((p: any) => p.id));
  return (
    <>
      <p className="muted">{user.company || 'External account'} — tick the jobs they may see.</p>
      {projects.map(p => (
        <label key={p.id} style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
          <input type="checkbox" className="checkbox" checked={ids.includes(p.id)}
            onChange={e => setIds(prev => e.target.checked ? [...prev, p.id] : prev.filter(x => x !== p.id))} />
          {' '}{p.name}
        </label>
      ))}
      {ids.length === 0 && (
        <p style={{ color: 'var(--danger)', fontSize: 13 }}>
          With no jobs ticked this account will be able to see nothing at all.
        </p>
      )}
      <div className="btn-row form-actions">
        <button className="btn btn-primary" onClick={() => onSave(ids)}>Save</button>
        <button className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </>
  );
}

function Backups() {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<string | null>(null);
  const [meta, setMeta] = useState<any | null>(null);
  const [sampleBusy, setSampleBusy] = useState(false);

  const load = () => {
    api('/backup/list').then(setData).catch(e => setError([e.message]));
    api('/meta').then(setMeta).catch(() => {});
  };
  useEffect(load, []);

  if (!data) return <Spinner />;
  return (
    <div>
      <h2>Backups &amp; data</h2>
      <ErrorBox error={error} onDismiss={() => setError(null)} />
      {msg && <div className="panel" style={{ borderLeft: '4px solid var(--olive)' }}>{msg}</div>}
      <div className="panel">
        <h3>Locations</h3>
        <p style={{ fontSize: 13 }}>Database: <code>{meta?.db_path}</code><br />Backups: <code>{data.dir}</code></p>
        <p className="muted">A backup is taken automatically once per day when the app is running, and before any database upgrade. The latest {data.keep} automatic backups are kept.</p>
        <div className="btn-row">
          <button className="btn btn-primary" onClick={async () => {
            try { const r = await api('/backup/now', { method: 'POST' }); setMsg(`Backup created: ${r.file}`); load(); }
            catch (e: any) { setError([e.message]); }
          }}>Back up now</button>
          <button className="btn" onClick={() => download('/export/json')}>Export all data (JSON)</button>
          <label className="muted">Keep
            <input className="input" style={{ width: 70, margin: '0 6px' }} type="number" min={1} defaultValue={data.keep}
              onBlur={async e => {
                try { await api('/backup/settings', { method: 'POST', body: { keep: Number(e.target.value) } }); load(); }
                catch (err: any) { setError([err.message]); }
              }} />
            automatic backups</label>
        </div>
      </div>
      <div className="panel">
        <h3>Backup history ({data.files.length})</h3>
        <div className="table-scroll" style={{ maxHeight: 300 }}>
          <table>
            <thead><tr><th>File</th><th>Size</th><th>Created</th><th></th></tr></thead>
            <tbody>
              {data.files.map((f: any) => (
                <tr key={f.file}>
                  <td>{f.file}</td>
                  <td>{Math.round(f.size / 1024)} KB</td>
                  <td>{f.modified.replace('T', ' ').slice(0, 19)}</td>
                  <td><button className="btn btn-sm" onClick={() => setConfirmRestore(f.file)}>Restore</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="panel">
        <h3>Sample data</h3>
        <p className="muted">Fictional demonstration data (names like “Charlie Chisel”). Safe to remove at any time — real records are untouched.</p>
        <div className="btn-row">
          <button className="btn" disabled={sampleBusy} onClick={async () => {
            setSampleBusy(true);
            try { await api('/sample/load', { method: 'POST' }); setMsg('Sample data loaded.'); }
            catch (e: any) { setError([e.message]); } finally { setSampleBusy(false); }
          }}>Load sample data</button>
          <button className="btn btn-danger" disabled={sampleBusy} onClick={async () => {
            setSampleBusy(true);
            try { await api('/sample/remove', { method: 'POST' }); setMsg('Sample data removed.'); }
            catch (e: any) { setError([e.message]); } finally { setSampleBusy(false); }
          }}>Remove sample data</button>
        </div>
      </div>
      {confirmRestore && (
        <Confirm
          message={`Restore backup "${confirmRestore}"?\n\nThe current database will be backed up first (pre-restore backup), then replaced with this file. Everyone should be signed out while restoring.`}
          onYes={async () => {
            try {
              const r = await api('/backup/restore', { method: 'POST', body: { file: confirmRestore } });
              setMsg(`Restored ${r.restored}. A safety copy of the previous data was saved as ${r.safety_backup}.`);
            } catch (e: any) { setError(e.errors?.length ? e.errors : [e.message]); }
            setConfirmRestore(null);
            load();
          }}
          onNo={() => setConfirmRestore(null)} />
      )}
    </div>
  );
}

function Audit() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [table, setTable] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api(`/audit${table ? `?table=${table}` : ''}`).then(setRows).catch(e => setError(e.message));
  }, [table]);
  if (error) return <ErrorBox error={error} />;
  return (
    <div>
      <div className="page-head">
        <h2>Change history</h2>
        <select className="input" value={table} onChange={e => setTable(e.target.value)}>
          <option value="">All records</option>
          <option value="attendance_periods">Attendance periods</option>
          <option value="accommodation_bookings">Accommodation bookings</option>
          <option value="booking_allocations">Room allocations</option>
          <option value="leave_records">Leave</option>
          <option value="people">Personnel</option>
        </select>
      </div>
      {!rows ? <Spinner /> : (
        <div className="table-wrap">
          <div className="table-scroll">
            <table>
              <thead><tr><th>When</th><th>Record</th><th>Action</th><th>Field</th><th>Old</th><th>New</th><th>By</th><th>Reason</th></tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={8} className="muted" style={{ padding: 20 }}>No changes recorded yet.</td></tr>}
                {rows.map(a => (
                  <tr key={a.id}>
                    <td className="muted">{a.changed_at}</td>
                    <td>{a.table_name.replace(/_/g, ' ')} #{a.record_id}</td>
                    <td>{a.action}</td>
                    <td>{a.field || ''}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.old_value || ''}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.new_value || ''}</td>
                    <td>{a.changed_by || ''}</td>
                    <td>{a.reason || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Exported so job-scoped accounts, which cannot open Settings at all, still have a way
// to change their own password.
export function Account() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string[] | null>(null);
  return (
    <div className="panel" style={{ maxWidth: 420 }}>
      <h3>Change my password</h3>
      <ErrorBox error={error} onDismiss={() => setError(null)} />
      {msg && <p style={{ color: 'var(--olive-dark)', fontWeight: 600 }}>{msg}</p>}
      <Field label="Current password"><input className="input" type="password" value={current} onChange={e => setCurrent(e.target.value)} /></Field>
      <div style={{ height: 8 }} />
      <Field label="New password (min 8 characters)"><input className="input" type="password" value={next} onChange={e => setNext(e.target.value)} /></Field>
      <div className="btn-row form-actions">
        <button className="btn btn-primary" onClick={async () => {
          setError(null); setMsg(null);
          try {
            await api('/users/change-password', { method: 'POST', body: { current_password: current, new_password: next } });
            setMsg('Password changed.');
            setCurrent(''); setNext('');
          } catch (e: any) { setError(e.errors?.length ? e.errors : [e.message]); }
        }}>Change password</button>
      </div>
      <p className="muted">Signed in as {currentUser?.display_name} ({currentUser?.role}).</p>
    </div>
  );
}
